import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { licenseService } from '../services/api';
import { useUser } from '../components/UserProvider';

export interface ActiveLicenseItem {
  id?: number;
  license_id: string;
  serial_number: string;
  license_type: string;
  camera_limit: number;
  max_ip_speakers?: number;
  max_announcements?: number;
  features: string[];
  issued_date?: string;
  expiry_date?: string;
  days_remaining?: number | null;
  customer_name?: string;
  company?: string;
  status: string;
}

interface LicenseState {
  features: string[];
  cameraLimit: number;
  maxIpSpeakers: number;
  maxAnnouncements: number;
  usedCameras: number;
  valid: boolean;
  loaded: boolean;
  activeLicenses: ActiveLicenseItem[];
  totalActiveLicenses: number;
  hasFeature: (key: string) => boolean;
  refresh: () => void;
}

const BASE_VMS_FEATURES = new Set([
  'dashboard', 'cameras', 'live_view', 'playback', 'camera_monitor',
  'gis_floor_map', 'notifications', 'recording'
]);

const LicenseContext = createContext<LicenseState>({
  features: [],
  cameraLimit: 0,
  maxIpSpeakers: 0,
  maxAnnouncements: 0,
  usedCameras: 0,
  valid: false,
  loaded: false,
  activeLicenses: [],
  totalActiveLicenses: 0,
  hasFeature: () => false,
  refresh: () => {},
});

export function LicenseProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const [features, setFeatures] = useState<string[]>([]);
  const [cameraLimit, setCameraLimit] = useState(0);
  const [maxIpSpeakers, setMaxIpSpeakers] = useState(0);
  const [maxAnnouncements, setMaxAnnouncements] = useState(0);
  const [usedCameras, setUsedCameras] = useState(0);
  const [valid, setValid] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeLicenses, setActiveLicenses] = useState<ActiveLicenseItem[]>([]);
  const [totalActiveLicenses, setTotalActiveLicenses] = useState(0);

  const fetchLicense = useCallback(async () => {
    try {
      const data = await licenseService.getInfo();
      const lic = data.license ?? {};
      setValid(lic.valid ?? false);
      setFeatures(lic.features ?? []);
      setCameraLimit(lic.camera_limit ?? 0);
      setMaxIpSpeakers(lic.max_ip_speakers ?? 0);
      setMaxAnnouncements(lic.max_announcements ?? 0);
      setUsedCameras(lic.used_cameras ?? 0);
      setActiveLicenses(lic.active_licenses ?? []);
      setTotalActiveLicenses(lic.total_active_licenses ?? (lic.active_licenses ? lic.active_licenses.length : (lic.valid ? 1 : 0)));
    } catch {
      // silently fail — keep defaults (allow-all)
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchLicense();
    } else {
      setFeatures([]);
      setValid(false);
      setCameraLimit(0);
      setMaxIpSpeakers(0);
      setMaxAnnouncements(0);
      setUsedCameras(0);
      setActiveLicenses([]);
      setTotalActiveLicenses(0);
      setLoaded(true);
    }
  }, [user?.id, fetchLicense]);

  const hasFeature = (key: string): boolean => {
    // No valid license or not yet loaded → strictly deny access
    if (!loaded || !valid) return false;
    // Dashboard is always accessible on any valid license
    if (key === 'dashboard') return true;
    // If features array is specified on the license, check entitlement strictly
    if (features.length > 0) {
      return features.includes(key);
    }
    // If legacy license with no features array specified, fall back to base VMS features
    return BASE_VMS_FEATURES.has(key);
  };

  return (
    <LicenseContext.Provider value={{
      features,
      cameraLimit,
      maxIpSpeakers,
      maxAnnouncements,
      usedCameras,
      valid,
      loaded,
      activeLicenses,
      totalActiveLicenses,
      hasFeature,
      refresh: fetchLicense
    }}>
      {children}
    </LicenseContext.Provider>
  );
}

export const useLicense = () => useContext(LicenseContext);
